import { cn, NoddiIcon } from 'noddi-ui-common';
import { ApiErrorMessageInternal, NoddiButton, NoddiSheet, noddiToast } from 'noddi-ui-web';
import { useState } from 'react';
import { NavioLicensePlate } from '../CarDetails/Cards/NavioLicensePlate';
import { CarDriverPicker } from './CarDriverPicker';
import { CarDriverSummary } from './CarDriverSummary';
import { buildDriverChange, driverChangeMessage, toCarManagerPatches } from './driverChangePlan';
import { DriverChangeReview } from './DriverChangeReview';
import type { CarGroup, MemberGroup } from './notificationGroupTypes';
import type { NotificationGroupOverviewTranslations } from './notificationTranslations';
import { defaultNotificationGroupOverviewTranslations } from './notificationTranslations';
import { RestrictionNote } from './RestrictionNote';
import { useDriverCandidates } from './useDriverCandidates';
import { useDriverChange } from './useDriverChange';
import { useNotificationOverviewCars } from './useNotificationOverviewCars';

type Props = {
  userGroupId: number;
  car: CarGroup;
  translations?: NotificationGroupOverviewTranslations;
};

export const CarContactButton = ({
  userGroupId,
  car,
  className,
  translations: t = defaultNotificationGroupOverviewTranslations
}: Props & { className?: string }) => {
  const [open, setOpen] = useState(false);

  return (
    // oxlint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions -- wrapper that stops the driver button inside it from also triggering the surrounding row's click
    <div className='flex min-w-0 items-center gap-1' onClick={(event) => event.stopPropagation()}>
      <button
        type='button'
        aria-label={t.editDriver}
        onClick={() => setOpen(true)}
        className={cn(
          'flex min-w-0 flex-1 items-center gap-2 rounded-md px-2 py-1.5 text-left transition-colors hover:bg-accent focus-visible:ring-2 focus-visible:ring-grey focus-visible:outline-hidden',
          className
        )}
      >
        {/* Without the label this is just a person's name beside the car's other people, and reads as one of them. */}
        <span className='shrink-0 text-xs font-medium text-muted-foreground'>{t.driver}</span>
        <CarDriverSummary car={car} translations={t} />
        <NoddiIcon name='ChevronRight' size='xSmall' className='ml-auto shrink-0 text-muted-foreground' />
      </button>

      {open && (
        <CarContactSheet userGroupId={userGroupId} car={car} translations={t} open={open} onOpenChange={setOpen} />
      )}
    </div>
  );
};

type SheetProps = Props & {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onAssigned?: (userGroupMemberId: number) => void;
  /** Choosing the driver is the group admin's job; everyone else gets the list read-only. Staff callers leave it on. */
  canEditDriver?: boolean;
  /** See `CarContactsOverview` — lets the driver picker link each candidate to their detail page. */
  getMemberDetailUrl?: (member: { userId: number }) => string;
};

/** Choose, then agree to what the choice does. */
type Stage = 'choose' | 'review';

export const CarContactSheet = ({
  userGroupId,
  car,
  open,
  onOpenChange,
  onAssigned,
  canEditDriver = true,
  getMemberDetailUrl,
  translations: t = defaultNotificationGroupOverviewTranslations
}: SheetProps) => {
  const [stage, setStage] = useState<Stage>('choose');

  const { data: currentCars, error: currentCarError } = useNotificationOverviewCars(userGroupId, [car.carId]);
  const currentCar = currentCars?.results[0] ?? car;
  const currentDriverId = currentCar.manager?.userGroupMemberId;
  const candidates = useDriverCandidates(userGroupId);
  // Picking only marks a row. Assigning on click took the car off whoever held it
  // before anyone could check they had hit the right person.
  const [pickedMember, setPickedMember] = useState<MemberGroup | undefined>(currentCar.manager ?? undefined);
  // The pick is the row as clicked. Read back from the list, which refetches after the review's
  // "Turn on SMS", so that warning clears; the snapshot only covers a pick off the current page.
  const selectedMember =
    candidates.members.find((member) => member.userGroupMemberId === pickedMember?.userGroupMemberId) ?? pickedMember;
  const { apply, isPending: isSaving, error } = useDriverChange({ userGroupId });
  const change = selectedMember ? buildDriverChange({ car: currentCar, incoming: selectedMember }) : undefined;

  const hasPendingChange = !!change && selectedMember?.userGroupMemberId !== currentDriverId;

  const confirm = async () => {
    if (!change || !(await apply(toCarManagerPatches([change])))) {
      return;
    }
    // Named rather than the generic "Successfully updated", which the review already earned the right to skip.
    noddiToast.success(driverChangeMessage([change], t));
    onAssigned?.(change.incoming.userGroupMemberId);
    onOpenChange(false);
  };

  const description = (() => {
    if (!canEditDriver) {
      return t.driverSheetDescription;
    }
    return stage === 'choose' ? t.chooseDriverStep : t.reviewStep;
  })();

  const footer = (() => {
    if (!canEditDriver) {
      return undefined;
    }
    if (stage === 'review') {
      return (
        <div className='flex w-full gap-2'>
          <NoddiButton variant='outline' className='flex-1' disabled={isSaving} onClick={() => setStage('choose')}>
            {t.back}
          </NoddiButton>
          <NoddiButton className='flex-1' loading={isSaving} onClick={() => void confirm()}>
            {t.confirmDriverChange}
          </NoddiButton>
        </div>
      );
    }
    // Every step is [secondary] [primary] in the same place, so nothing moves between them.
    return (
      <div className='flex w-full gap-2'>
        <NoddiButton variant='outline' className='flex-1' onClick={() => onOpenChange(false)}>
          {t.cancel}
        </NoddiButton>
        <NoddiButton className='flex-1' disabled={!hasPendingChange} onClick={() => setStage('review')}>
          {t.continueToReview}
        </NoddiButton>
      </div>
    );
  })();

  return (
    <NoddiSheet
      open={open}
      onOpenChange={onOpenChange}
      title={
        // Mirrors the member sheet's avatar + name header, with the plate as the car's face.
        <span className='flex min-w-0 items-center gap-3'>
          <span className='flex size-8 shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground'>
            <NoddiIcon name='Car' size='small' />
          </span>
          <span className='flex min-w-0 flex-col gap-1'>
            <span className='truncate'>{`${car.make} ${car.model}`}</span>
            <NavioLicensePlate car={{ licensePlateNumber: car.licensePlate.number }} className='w-fit' />
          </span>
        </span>
      }
      description={description}
      closeText={t.cancel}
      footer={footer}
    >
      <div className='flex flex-col gap-4'>
        {!canEditDriver && <RestrictionNote leadIn={t.viewOnly} reasons={[t.driverEditAdminOnly]} />}

        {(error || currentCarError) && <ApiErrorMessageInternal error={[error, currentCarError]} />}

        {stage === 'review' && change ? (
          <DriverChangeReview
            userGroupId={userGroupId}
            changes={[change]}
            subject='car'
            canEditNotifications={canEditDriver}
            translations={t}
          />
        ) : (
          <CarDriverPicker
            candidates={candidates}
            currentDriverId={currentDriverId}
            selectedMember={selectedMember}
            onSelect={setPickedMember}
            disabled={!canEditDriver || isSaving}
            getMemberDetailUrl={getMemberDetailUrl}
            translations={t}
          />
        )}
      </div>
    </NoddiSheet>
  );
};
